import { Map as ImmutableMap } from 'immutable';
import { createSelector } from 'reselect';

import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { INVESTMENT_HOLDINGS_V2_REDUCER_KEY } from 'app-components/flux/investment-holdings-v2/investmentHoldingsV2Types';
import { SECURITIES_REDUCER_KEY, type Security } from 'app-components/flux/securities/securitiesTypes';
import { getUnknownSecurity } from 'app-components/flux/securities/securitiesUtils';
import type RootState from 'app-components/utils/redux-store/rootState';

import { INVESTMENT_QUOTES_V2_REDUCER_KEY } from './investmentQuotesV2Reducer';
import type { InvestmentQuotesV2Store, InvestmentQuoteV2 } from './investmentQuotesV2Types';
import { getQuoteBySymbol } from './investmentQuotesV2Utils';

type State = Pick<RootState, typeof INVESTMENT_QUOTES_V2_REDUCER_KEY>;
type SecuritiesState = Pick<RootState, typeof SECURITIES_REDUCER_KEY>;
type HoldingsState = Pick<RootState, typeof INVESTMENT_HOLDINGS_V2_REDUCER_KEY>;

export const getResourceStore = (state: State): InvestmentQuotesV2Store => state[INVESTMENT_QUOTES_V2_REDUCER_KEY];

export const getLoadPending = (state: State): boolean => getResourceStore(state).loadPending;
export const getIsLoading = (state: State): boolean => getResourceStore(state).isLoading;
export const getError = (state: State): unknown => getResourceStore(state).error;
export const getResourcesById = (state: State): ImmutableMap<string, InvestmentQuoteV2> =>
  getResourceStore(state).resourcesById;

export const getLastSyncDate = (state: State): string | null | undefined => getResourceStore(state).lastSyncDate;
const unknownSecurity = getUnknownSecurity();

// NOTE: this is to break circular dependencies
const getSecuritiesResourcesById = (state: SecuritiesState): ImmutableMap<string, Security> =>
  state[SECURITIES_REDUCER_KEY]?.resourcesById;
const getHoldingsResourcesById = (
  state: HoldingsState,
): RootState[typeof INVESTMENT_HOLDINGS_V2_REDUCER_KEY]['resourcesById'] =>
  state[INVESTMENT_HOLDINGS_V2_REDUCER_KEY].resourcesById;

export const getInvestmentQuoteBySymbol = createSelector(
  (state: State) => getResourcesById(state),
  (_state: State, symbol: string | null | undefined) => symbol,
  (quotes, symbol): InvestmentQuoteV2 | undefined => {
    return getQuoteBySymbol(quotes, symbol);
  },
);

export const getQuoteListByLocalSecurityId = createSelector(
  (state: SecuritiesState) => getSecuritiesResourcesById(state),
  (state: State) => getResourcesById(state),
  (securities, quotes): ImmutableMap<string, InvestmentQuoteV2 | Security> => {
    const fullList = securities.map((security) => {
      const symbol = security?.symbol || null;
      const quote = getQuoteBySymbol(quotes, symbol);
      return quote || security || unknownSecurity;
    });
    return ImmutableMap(fullList);
  },
);

export const getQuoteListByHoldingId = createSelector(
  (state: SecuritiesState) => getSecuritiesResourcesById(state),
  (state: HoldingsState) => getHoldingsResourcesById(state),
  (state: State) => getResourcesById(state),
  (state: Parameters<typeof featureFlagsSelectors.getFeatureFlag>[0]) =>
    featureFlagsSelectors.getFeatureFlag(state, 'relyOnSecurities'),
  (securities, holdings, quotes, securitiesFF): ImmutableMap<string, InvestmentQuoteV2 | Security | null> =>
    holdings.map((holding) => {
      const localSecurityId = holding?.localSecurityId;
      const security = localSecurityId === undefined ? undefined : securities?.get(localSecurityId);
      const symbol = securitiesFF ? security?.symbol || null : holding?.symbol || null;
      const quote = getQuoteBySymbol(quotes, symbol);
      if (!securitiesFF) {
        return quote || null;
      }
      return quote || security || unknownSecurity;
    }),
);
